import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import MDTypography from "components/MDTypography";
import dayjs from "dayjs";
import { CustomJobPhase } from "hooks/jobs/job-phases/useGetJobPhase";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import {
  formatJobCostingUnits,
  getCostItemTotal,
  SummaryMetrics,
  VarianceDisplay,
} from "./jobCostingSummaryUtils";

type CostItem = NonNullable<CustomJobPhase["costItems"]>[number];

const tableSx = {
  minWidth: 0,
  width: "100%",
  tableLayout: "auto",
  "& th, & td": {
    px: 1.5,
    py: 1.25,
    whiteSpace: "nowrap",
    verticalAlign: "top",
  },
  "& th": {
    fontSize: "0.72rem",
    letterSpacing: "0.02em",
  },
  "& td": {
    fontSize: "0.8rem",
  },
} as const;

const columns = [
  { key: "entry", label: "Entry", width: "18%" },
  { key: "materialUnits", label: "Mat. Units", width: "10%" },
  { key: "material", label: "Material", width: "11%" },
  { key: "labor", label: "Labor", width: "11%" },
  { key: "equipment", label: "Equip.", width: "11%" },
  { key: "additional", label: "Additional", width: "12%" },
  { key: "subcontractor", label: "Subcontract", width: "12%" },
  { key: "trucking", label: "Trucking", width: "11%" },
  { key: "total", label: "Total Cost", width: "16%" },
] as const;

function JobCostingTableHeader() {
  return (
    <TableHead sx={{ display: "table-header-group" }}>
      <TableRow>
        {columns.map((column) => (
          <TableCell key={column.key} sx={{ width: column.width }}>
            {column.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

function JobCostingEntryRow({ costItem }: { costItem: CostItem }) {
  return (
    <TableRow>
      <TableCell>
        <MDTypography display="block" fontSize="0.8rem" fontWeight="medium">
          {dayjs(costItem?.date).format("MM/DD/YYYY")}
        </MDTypography>
        <MDTypography display="block" fontSize="0.72rem" color="text">
          {costItem?.user?.name ?? "Unassigned"}
        </MDTypography>
      </TableCell>
      <TableCell>{formatJobCostingUnits(costItem?.material?.materialNeeded ?? 0)}</TableCell>
      <TableCell>{formatCentsToUSD(costItem?.costMaterial ?? 0)}</TableCell>
      <TableCell>{formatCentsToUSD(costItem?.costLabour ?? 0)}</TableCell>
      <TableCell>{formatCentsToUSD(costItem?.costEquipment ?? 0)}</TableCell>
      <TableCell>{formatCentsToUSD(costItem?.costAdditional ?? 0)}</TableCell>
      <TableCell>-</TableCell>
      <TableCell>{formatCentsToUSD(costItem?.costTransferTruck ?? 0)}</TableCell>
      <TableCell sx={{ fontWeight: 600 }}>{formatCentsToUSD(getCostItemTotal(costItem))}</TableCell>
    </TableRow>
  );
}

function EmptyCostingRow() {
  return (
    <TableRow>
      <TableCell colSpan={9}>
        <MDTypography variant="button" color="text">
          No job costs have been entered yet.
        </MDTypography>
      </TableCell>
    </TableRow>
  );
}

function SummaryValue({ value, bold = false }: { value: string; bold?: boolean }) {
  return (
    <TableCell sx={{ fontWeight: 700, color: bold ? "text.primary" : undefined }}>{value}</TableCell>
  );
}

function VarianceValue({ value, bold = false }: { value: VarianceDisplay; bold?: boolean }) {
  return (
    <TableCell>
      <MDTypography fontSize="13px" fontWeight={bold ? "bold" : "medium"} color={value.color}>
        {value.display}
      </MDTypography>
    </TableCell>
  );
}

function JobCostingSummaryRows({ summary }: { summary: SummaryMetrics }) {
  return (
    <>
      <TableRow sx={{ backgroundColor: "#f8fafc" }}>
        <TableCell sx={{ fontWeight: 700 }}>Total Actuals</TableCell>
        <SummaryValue value={formatJobCostingUnits(summary.actualMaterialUnits)} />
        <SummaryValue value={formatCentsToUSD(summary.actualMaterialCost)} />
        <SummaryValue value={formatCentsToUSD(summary.actualLaborCost)} />
        <SummaryValue value={formatCentsToUSD(summary.actualEquipmentCost)} />
        <SummaryValue value={formatCentsToUSD(summary.actualAdditionalCost)} />
        <SummaryValue value={formatCentsToUSD(summary.actualSubcontractorCost)} />
        <SummaryValue value={formatCentsToUSD(summary.actualTruckingCost)} />
        <SummaryValue value={formatCentsToUSD(summary.actualTotalCost)} bold />
      </TableRow>
      <TableRow sx={{ backgroundColor: "#fcfcfd" }}>
        <TableCell sx={{ fontWeight: 700 }}>Proposed</TableCell>
        <SummaryValue value={formatJobCostingUnits(summary.proposedMaterialUnits)} />
        <SummaryValue value={formatCentsToUSD(summary.proposedMaterialCost)} />
        <SummaryValue value={formatCentsToUSD(summary.proposedLaborCost)} />
        <SummaryValue value={formatCentsToUSD(summary.proposedEquipmentCost)} />
        <SummaryValue value={formatCentsToUSD(summary.proposedAdditionalCost)} />
        <SummaryValue value={formatCentsToUSD(summary.proposedSubcontractorCost)} />
        <SummaryValue value={formatCentsToUSD(summary.proposedTruckingCost)} />
        <SummaryValue value={formatCentsToUSD(summary.proposedTotalCost)} bold />
      </TableRow>
      <TableRow sx={{ backgroundColor: "#f3f6fb" }}>
        <TableCell sx={{ fontWeight: 700 }}>Variance</TableCell>
        <VarianceValue value={summary.varianceMaterialUnits} />
        <VarianceValue value={summary.varianceMaterialCost} />
        <VarianceValue value={summary.varianceLaborCost} />
        <VarianceValue value={summary.varianceEquipmentCost} />
        <VarianceValue value={summary.varianceAdditionalCost} />
        <VarianceValue value={summary.varianceSubcontractorCost} />
        <VarianceValue value={summary.varianceTruckingCost} />
        <VarianceValue value={summary.varianceTotalCost} bold />
      </TableRow>
    </>
  );
}

export default function JobCostingSummaryTable({
  costItems,
  summary,
}: {
  costItems: CustomJobPhase["costItems"];
  summary: SummaryMetrics;
}) {
  return (
    <Table size="small" sx={tableSx}>
      <JobCostingTableHeader />
      <TableBody>
        {costItems?.map((costItem, index) => (
          <JobCostingEntryRow key={index} costItem={costItem} />
        ))}
        {costItems?.length === 0 && <EmptyCostingRow />}
        <JobCostingSummaryRows summary={summary} />
      </TableBody>
    </Table>
  );
}
